import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Box,
  Button,
  TextField,
  Typography,
  Card,
  CardContent,
  Grid,
  MenuItem,
  CircularProgress,
  IconButton,
  Alert,
  Tooltip,
  Checkbox,
  Pagination
} from '@mui/material';
import {
  Add,
  Edit,
  Delete,
  Search,
  Inventory,
  Visibility,
  VisibilityOff,
  FileUpload,
  FileDownload,
  DeleteSweep,
  Save
} from '@mui/icons-material';
import productService from '../services/productService';
import categoryService from '../services/categoryService';
import brandService from '../services/brandService';
import uploadService from '../services/uploadService';
import ProductFormDialog from '../components/ProductFormDialog';
import AdminPageHeader from '../components/AdminPageHeader';
import { useAuth } from '../context/AuthContext';

const Products = () => {
  const { user } = useAuth();
  const canBulkManage = user?.role === 'admin';
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [brands, setBrands] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingProducts, setLoadingProducts] = useState(false);
  const [openDialog, setOpenDialog] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [filterCategory, setFilterCategory] = useState('');
  const [filterBrand, setFilterBrand] = useState('');
  const [message, setMessage] = useState({ type: '', text: '' });
  const searchTimeoutRef = useRef(null);
  const importInputRef = useRef(null);
  const [transferring, setTransferring] = useState(false);
  const [selectedProducts, setSelectedProducts] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  const [pagination, setPagination] = useState({ total: 0, totalPages: 1 });
  const [stockDrafts, setStockDrafts] = useState({});

  // Debounce para el campo de búsqueda
  useEffect(() => {
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    searchTimeoutRef.current = setTimeout(() => {
      setDebouncedSearch(search);
      setCurrentPage(1);
    }, 300); // Espera 300ms después de que el usuario deja de escribir

    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, [search]);

  // Cargar categorías y marcas solo una vez al inicio
  useEffect(() => {
    loadCategoriesAndBrands();
  }, []);

  // Cargar productos cuando cambian los filtros
  useEffect(() => {
    loadProducts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSearch, filterCategory, filterBrand, currentPage, pageSize]);

  const loadCategoriesAndBrands = async () => {
    setLoading(true);
    const [categoriesRes, brandsRes] = await Promise.all([
      categoryService.getAll({ active: true }),
      brandService.getAll({ active: true })
    ]);

    if (categoriesRes.success) setCategories(categoriesRes.data);
    if (brandsRes.success) setBrands(brandsRes.data);
    setLoading(false);
  };

  const loadProducts = useCallback(async () => {
    setLoadingProducts(true);
    
    const params = {
      search: debouncedSearch || undefined,
      categoryId: filterCategory || undefined,
      brandId: filterBrand || undefined,
      page: currentPage,
      limit: pageSize
    };

    const productsRes = await productService.getAll(params);

    if (productsRes.success) {
      setProducts(productsRes.data);
      setPagination({
        total: productsRes.pagination?.total || productsRes.data.length,
        totalPages: productsRes.pagination?.totalPages || 1
      });
      const visibleIds = new Set(productsRes.data.map((product) => product.id));
      setSelectedProducts((current) => current.filter((id) => visibleIds.has(id)));
    }

    setLoadingProducts(false);
  }, [debouncedSearch, filterCategory, filterBrand, currentPage, pageSize]);

  const handleOpenDialog = useCallback((product = null) => {
      setEditingProduct(product);
    setOpenDialog(true);
  }, []);

  const handleCloseDialog = useCallback(() => {
    setOpenDialog(false);
    setEditingProduct(null);
  }, []);

  const handleSubmit = useCallback(async (formData, editingProduct) => {
    try {
      const productData = {
        ...formData,
        price: parseFloat(formData.price),
        cost: parseFloat(formData.cost) || 0,
        precioLista1: parseFloat(formData.precioLista1) || 0,
        precioLista2: parseFloat(formData.precioLista2) || 0,
        precioLista3: parseFloat(formData.precioLista3) || 0,
        stock: parseInt(formData.stock),
        minStock: parseInt(formData.minStock)
      };

      let result;
      if (editingProduct) {
        result = await productService.update(editingProduct.id, productData);
      } else {
        result = await productService.create(productData);
      }

      if (result.success) {
        setMessage({ type: 'success', text: result.message });
        handleCloseDialog();
        loadProducts();
      } else {
        setMessage({ type: 'error', text: result.message });
      }
    } catch (error) {
      setMessage({ type: 'error', text: 'Error al guardar la etiqueta o bebida' });
    }
  }, [loadProducts, handleCloseDialog]);

  const handleDelete = async (id) => {
    if (window.confirm('¿Estás seguro de eliminar esta etiqueta o bebida?')) {
      const result = await productService.delete(id);
      if (result.success) {
        setMessage({ type: 'success', text: result.message });
        loadProducts();
      } else {
        setMessage({ type: 'error', text: result.message });
      }
    }
  };

  const handleCreateCategory = useCallback(async (name) => {
    const result = await categoryService.create({ name });
    if (!result.success) {
      setMessage({ type: 'error', text: result.message });
      return null;
    }
    setCategories((current) => [...current, result.data].sort((a, b) => a.name.localeCompare(b.name, 'es')));
    setMessage({ type: 'success', text: result.message });
    return result.data;
  }, []);

  const handleCreateBrand = useCallback(async (name) => {
    const result = await brandService.create({ name });
    if (!result.success) {
      setMessage({ type: 'error', text: result.message });
      return null;
    }
    setBrands((current) => [...current, result.data].sort((a, b) => a.name.localeCompare(b.name, 'es')));
    setMessage({ type: 'success', text: result.message });
    return result.data;
  }, []);

  const handleToggleVisibility = async (product) => {
    const showInCatalog = !product.showInCatalog;
    const result = await productService.update(product.id, { showInCatalog });
    if (result.success) {
      setProducts((current) => current.map((item) =>
        item.id === product.id ? { ...item, showInCatalog } : item
      ));
      setMessage({ type: 'success', text: showInCatalog ? 'Producto visible en el catálogo' : 'Producto oculto del catálogo' });
    } else {
      setMessage({ type: 'error', text: result.message });
    }
  };

  const handleExport = async () => {
    setTransferring(true);
    const result = await productService.exportFile();
    if (result.success) {
      const url = URL.createObjectURL(result.data);
      const link = document.createElement('a');
      link.href = url;
      link.download = `productos-${new Date().toISOString().slice(0, 10)}.xlsx`;
      link.click();
      URL.revokeObjectURL(url);
    } else {
      setMessage({ type: 'error', text: result.message });
    }
    setTransferring(false);
  };

  const handleImport = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;
    setTransferring(true);
    const result = await productService.importFile(file);
    setMessage({ type: result.success ? 'success' : 'error', text: result.message });
    if (result.success) {
      await Promise.all([loadProducts(), loadCategoriesAndBrands()]);
    }
    setTransferring(false);
  };

  const handleSelectProduct = (id) => {
    setSelectedProducts((current) => current.includes(id) ? current.filter((item) => item !== id) : [...current, id]);
  };

  const handleSelectAll = () => {
    setSelectedProducts((current) => current.length === products.length ? [] : products.map((product) => product.id));
  };

  const handleBulkDelete = async () => {
    if (!selectedProducts.length) return;
    if (!window.confirm(`¿Eliminar los ${selectedProducts.length} productos seleccionados? Esta acción no se puede deshacer.`)) return;
    setTransferring(true);
    const result = await productService.bulkDelete(selectedProducts);
    setMessage({ type: result.success ? 'success' : 'error', text: result.message });
    if (result.success) {
      setSelectedProducts([]);
      await loadProducts();
    }
    setTransferring(false);
  };

  const handleBulkVisibility = async (showInCatalog) => {
    if (!selectedProducts.length) return;
    setTransferring(true);
    const result = await productService.bulkUpdateVisibility(selectedProducts, showInCatalog);
    setMessage({ type: result.success ? 'success' : 'error', text: result.message });
    if (result.success) {
      setProducts((current) => current.map((product) => selectedProducts.includes(product.id)
        ? { ...product, showInCatalog }
        : product));
      setSelectedProducts([]);
    }
    setTransferring(false);
  };

  const handleSaveStock = async (product) => {
    const nextStock = Number.parseInt(stockDrafts[product.id], 10);
    if (!Number.isInteger(nextStock) || nextStock < 0) {
      setMessage({ type: 'error', text: 'El stock debe ser un número entero mayor o igual a cero' });
      return;
    }
    if (!window.confirm(`¿Guardar stock ${nextStock} para “${product.name}”?`)) return;
    const result = await productService.updateStock(product.id, nextStock, 'set');
    if (result.success) {
      setProducts((current) => current.map((item) => item.id === product.id ? { ...item, stock: nextStock } : item));
      setStockDrafts((current) => { const next = { ...current }; delete next[product.id]; return next; });
      setMessage({ type: 'success', text: `Stock de ${product.name} actualizado a ${nextStock}` });
    } else {
      setMessage({ type: 'error', text: result.message });
    }
  };

  const getStockColor = (product) => {
    if (product.stock === 0) return 'error';
    if (product.stock <= product.minStock) return 'warning';
    return 'success';
  };

  if (loading) {
    return (
      <Box className="d-flex justify-center align-center min-h-60vh">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box className="admin-page">
      <AdminPageHeader
        eyebrow="INVENTARIO"
        title="Etiquetas y bebidas"
        subtitle="Administrá el surtido, precios y disponibilidad de la vinoteca."
        icon={<Inventory />}
        action={<Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          <input ref={importInputRef} type="file" accept=".xlsx,.xls" hidden onChange={handleImport} />
          <Button variant="outlined" startIcon={<FileUpload />} onClick={() => importInputRef.current?.click()} disabled={transferring}>Importar</Button>
          <Button variant="outlined" startIcon={<FileDownload />} onClick={handleExport} disabled={transferring}>Exportar</Button>
          {canBulkManage && selectedProducts.length > 0 && <>
            <Button color="success" variant="outlined" startIcon={<Visibility />} onClick={() => handleBulkVisibility(true)} disabled={transferring}>Mostrar ({selectedProducts.length})</Button>
            <Button color="warning" variant="outlined" startIcon={<VisibilityOff />} onClick={() => handleBulkVisibility(false)} disabled={transferring}>Ocultar ({selectedProducts.length})</Button>
            <Button color="error" variant="contained" startIcon={<DeleteSweep />} onClick={handleBulkDelete} disabled={transferring}>Eliminar ({selectedProducts.length})</Button>
          </>}
          <Button variant="contained" startIcon={<Add />} onClick={() => handleOpenDialog()}>Nueva etiqueta</Button>
        </Box>}
      />

      {message.text && (
        <Alert severity={message.type} onClose={() => setMessage({ type: '', text: '' })} className="mb-md">
          {message.text}
        </Alert>
      )}

      {/* Filtros */}
      <Card className="mb-lg">
        <CardContent>
          <Grid container spacing={2}>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                placeholder="Buscar etiqueta, bodega o bebida..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                disabled={loadingProducts}
                InputProps={{
                  startAdornment: <Search className="mr-sm" color="action" />
                }}
                helperText={loadingProducts ? "Buscando..." : ""}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                select
                fullWidth
                label="Categoría"
                value={filterCategory}
                onChange={(e) => { setFilterCategory(e.target.value); setCurrentPage(1); }}
                disabled={loadingProducts}
              >
                <MenuItem value="">Todas las categorías</MenuItem>
                {categories.map((cat) => (
                  <MenuItem key={cat.id} value={cat.id}>
                    {cat.name}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                select
                fullWidth
                label="Marca"
                value={filterBrand}
                onChange={(e) => { setFilterBrand(e.target.value); setCurrentPage(1); }}
                disabled={loadingProducts}
              >
                <MenuItem value="">Todas las marcas</MenuItem>
                {brands.map((brand) => (
                  <MenuItem key={brand.id} value={brand.id}>
                    {brand.name}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Contador de productos */}
      <Box className="mb-md d-flex align-center" sx={{ gap: 1, flexWrap: 'wrap' }}>
        <Typography variant="body2" color="text.secondary">
          {pagination.total > 0
            ? `Mostrando ${(currentPage - 1) * pageSize + 1}–${Math.min(currentPage * pageSize, pagination.total)} de ${pagination.total} productos`
            : 'No hay productos para mostrar'}
        </Typography>
        {loadingProducts && <CircularProgress size={16} />}
      </Box>

      {/* Tabla de productos */}
      <Card>
        <CardContent>
          <Box className="table-container" sx={{ position: 'relative', opacity: loadingProducts ? 0.6 : 1 }}>
            <table className="table table-responsive">
              <thead>
                <tr>
                  {canBulkManage && <th className="text-center"><Checkbox size="small" checked={products.length > 0 && selectedProducts.length === products.length} indeterminate={selectedProducts.length > 0 && selectedProducts.length < products.length} onChange={handleSelectAll} /></th>}
                  <th>Imagen</th>
                  <th>Código</th>
                  <th>Nombre</th>
                  <th>Categoría</th>
                  <th>Marca</th>
                  <th className="text-right">Precio</th>
                  <th className="text-center">Stock</th>
                  <th className="text-center">Acciones</th>
                </tr>
              </thead>
              <tbody>
                {products.length === 0 && !loadingProducts ? (
                  <tr>
                    <td colSpan={canBulkManage ? 9 : 8} className="text-center">
                      No hay etiquetas o bebidas para mostrar
                    </td>
                  </tr>
                ) : (
                  products.map((product) => (
                    <tr key={product.id}>
                      {canBulkManage && <td className="text-center"><Checkbox size="small" checked={selectedProducts.includes(product.id)} onChange={() => handleSelectProduct(product.id)} /></td>}
                      <td>
                        <img
                          src={product.image ? uploadService.getImageUrl(product.image) : '/no-image.svg'}
                          alt={product.name}
                          loading="lazy"
                          className="img-thumbnail"
                          onClick={() => handleOpenDialog(product)}
                          role="button"
                          tabIndex={0}
                          onKeyDown={(event) => {
                            if (event.key === 'Enter' || event.key === ' ') handleOpenDialog(product);
                          }}
                          style={{ cursor: 'pointer' }}
                          onError={(e) => {
                            e.target.src = '/no-image.svg';
                          }}
                        />
                      </td>
                      <td>{product.code}</td>
                      <td>
                        <Button color="inherit" onClick={() => handleOpenDialog(product)} sx={{ p: 0, minWidth: 0, textTransform: 'none', justifyContent: 'flex-start' }}>
                          {product.name}
                        </Button>
                      </td>
                      <td>{product.category?.name || '-'}</td>
                      <td>{product.brand?.name || '-'}</td>
                      <td className="text-right">${product.price.toLocaleString('es-AR')}</td>
                      <td className="text-center">
                        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 0.5 }}>
                          <TextField
                            type="number"
                            size="small"
                            value={stockDrafts[product.id] ?? product.stock}
                            onChange={(event) => setStockDrafts((current) => ({ ...current, [product.id]: event.target.value }))}
                            onKeyDown={(event) => { if (event.key === 'Enter' && stockDrafts[product.id] !== undefined) handleSaveStock(product); }}
                            inputProps={{ min: 0, step: 1, style: { textAlign: 'center' } }}
                            color={getStockColor({ ...product, stock: Number(stockDrafts[product.id] ?? product.stock) })}
                            sx={{ width: 82 }}
                          />
                          {stockDrafts[product.id] !== undefined && Number(stockDrafts[product.id]) !== Number(product.stock) && (
                            <Tooltip title="Guardar stock">
                              <IconButton size="small" color="primary" onClick={() => handleSaveStock(product)}>
                                <Save fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          )}
                        </Box>
                      </td>
                      <td className="text-center">
                        <Tooltip title={product.showInCatalog ? 'Visible en catálogo. Click para ocultar' : 'Oculto del catálogo. Click para mostrar'}>
                          <IconButton size="small" color={product.showInCatalog ? 'success' : 'default'} onClick={() => handleToggleVisibility(product)}>
                            {product.showInCatalog ? <Visibility fontSize="small" /> : <VisibilityOff fontSize="small" />}
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Editar producto"><IconButton size="small" onClick={() => handleOpenDialog(product)}><Edit fontSize="small" /></IconButton></Tooltip>
                        <Tooltip title="Eliminar producto"><IconButton size="small" color="error" onClick={() => handleDelete(product.id)}><Delete fontSize="small" /></IconButton></Tooltip>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </Box>
        </CardContent>
      </Card>

      {pagination.total > 0 && (
        <Box sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: 2,
          flexWrap: 'wrap',
          position: 'sticky',
          bottom: 0,
          zIndex: 10,
          mt: 2,
          px: 2,
          py: 1.5,
          bgcolor: 'background.paper',
          borderTop: 1,
          borderColor: 'divider',
          boxShadow: '0 -4px 14px rgba(0, 0, 0, 0.16)'
        }}>
          <TextField
            select
            size="small"
            label="Por página"
            value={pageSize}
            onChange={(event) => { setPageSize(Number(event.target.value)); setCurrentPage(1); }}
            sx={{ width: 120 }}
          >
            {[25, 50, 250, 500].map((size) => <MenuItem key={size} value={size}>{size}</MenuItem>)}
          </TextField>
          <Pagination
            count={pagination.totalPages}
            page={currentPage}
            onChange={(_, page) => setCurrentPage(page)}
            color="primary"
            showFirstButton
            showLastButton
          />
        </Box>
      )}

      {/* Dialog de crear/editar */}
      <ProductFormDialog
        open={openDialog}
        editingProduct={editingProduct}
        categories={categories}
        brands={brands}
        onCreateCategory={handleCreateCategory}
        onCreateBrand={handleCreateBrand}
        onClose={handleCloseDialog}
        onSubmit={handleSubmit}
      />
    </Box>
  );
};

export default Products;

